import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { Icons } from '../shared/icons';

const LegalSection = ({ title, children }) => (
  <section className="legal-section">
    <h3 className="legal-section-title">{title}</h3>
    <div className="legal-section-content">{children}</div>
  </section>
);

const LegalList = ({ items }) => (
  <ul className="legal-list">
    {items.map((item, idx) => (
      <li key={idx}>{item}</li>
    ))}
  </ul>
);

const LastUpdated = () => (
  <p className="legal-updated">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
);

// ============================================================================
// TERMS OF SERVICE
// ============================================================================

export function TermsOfService() {
  return (
    <div className="legal-page">
      <div className="mk-container">
        <header className="legal-header">
          <Link to="/" className="legal-back">
            <SafeIcon icon={Icons.ArrowLeft} />
            Back to home
          </Link>
          <h1>Terms of Service</h1>
          <p className="legal-subtitle">
            These terms govern your use of the During client portal platform operated by during.uk
          </p>
          <LastUpdated />
        </header>

        <div className="legal-content">
          <LegalSection title="1. Acceptance of Terms">
            <p>
              By accessing or using the During client portal platform (the "Service"), operated by during.uk,
              you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms,
              please do not use the Service.
            </p>
          </LegalSection>

          <LegalSection title="2. Who We Are">
            <p>
              During is a client portal platform operated by during.uk, designed for UK heating and plumbing
              agencies. The platform provides web-based tools for invoice management, job scheduling, document
              sharing, and client communication.
            </p>
          </LegalSection>

          <LegalSection title="3. Description of Service">
            <p>
              The Service includes:
            </p>
            <LegalList items={[
              'A multi-tenant client portal where agency customers can view invoices, quotes, job history, and documents',
              'An engineer portal for on-site job sheet completion, certificate creation, and photo capture',
              'An admin console for agency staff to manage clients, jobs, invoicing, and team coordination',
              'Payment processing via Stripe for online invoice payments',
              'Secure document sharing and messaging capabilities'
            ]} />
          </LegalSection>

          <LegalSection title="4. Accounts and Access">
            <p>
              Access to the Service is provided through:
            </p>
            <LegalList items={[
              'Agency admin and staff accounts: created by your agency administrator, protected by email and password',
              'Client portal access: passwordless magic-link authentication delivered via email',
              'Engineer portal access: secure login with credentials provided by your agency',
              'You are responsible for maintaining the confidentiality of your account credentials',
              'You must notify us immediately if you suspect unauthorised access to your account'
            ]} />
          </LegalSection>

          <LegalSection title="5. Acceptable Use">
            <p>You agree not to use the Service to:</p>
            <LegalList items={[
              'Upload, store, or share illegal, harmful, threatening, abusive, defamatory, or otherwise objectionable content',
              'Attempt to gain unauthorised access to any part of the Service, other user accounts, or our systems',
              'Interfere with or disrupt the Service or servers connected to the Service',
              'Use the Service for any purpose other than its intended business use within your agency',
              'Violate any applicable local, national, or international law or regulation',
              'Transmit viruses, malware, or any other code that could harm the Service or its users'
            ]} />
          </LegalSection>

          <LegalSection title="6. Availability and Service Level">
            <p>
              We strive to maintain high availability of the Service, but we do not guarantee uninterrupted access.
              The Service is provided "as is" and we do not provide any express or implied warranties regarding
              availability, reliability, or performance. Scheduled maintenance may result in temporary unavailability.
            </p>
          </LegalSection>

          <LegalSection title="7. Payment and Billing">
            <p>
              Subscription plans and pricing are detailed on the{' '}
              <Link to="/pricing">pricing page</Link>. By subscribing to a paid plan, you agree to:
            </p>
            <LegalList items={[
              'Pay all applicable fees in accordance with the billing cycle applicable to your chosen plan',
              'Provide accurate, current, and complete payment information',
              'Maintain and promptly update your payment information to keep your account in good standing',
              'Fees are non-refundable except as expressly stated in our policies or as required by law'
            ]} />
            <p style={{ marginTop: 12 }}>
              We reserve the right to modify pricing for future subscriptions with 30 days notice to existing customers.
            </p>
          </LegalSection>

          <LegalSection title="8. Limitation of Liability">
            <p>
              To the fullest extent permitted by applicable law, during.uk shall not be liable for:
            </p>
            <LegalList items={[
              'Any indirect, incidental, special, consequential, or punitive damages',
              'Loss of profits, data, use, goodwill, or other intangible losses',
              'Damages resulting from your use or inability to use the Service',
              'Damages from unauthorised access to or alteration of your transmissions or data'
            ]} />
            <p style={{ marginTop: 12 }}>
              In no event shall our total liability to you exceed the amount you have paid to us in the twelve
              months preceding the claim.
            </p>
          </LegalSection>

          <LegalSection title="9. Termination">
            <p>
              We reserve the right to suspend or terminate your access to the Service at any time, with or without
              cause, with or without notice. You may also terminate your account at any time through your agency
              admin console or by contacting us. Upon termination:
            </p>
            <LegalList items={[
              'Your right to use the Service will immediately cease',
              'You will remain liable for all charges incurred prior to termination',
              'We may delete your data in accordance with our Privacy Policy and applicable retention periods'
            ]} />
          </LegalSection>

          <LegalSection title="10. Governing Law and Jurisdiction">
            <p>
              These Terms shall be governed by and construed in accordance with the laws of England and Wales.
              Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts
              of England and Wales.
            </p>
          </LegalSection>

          <LegalSection title="11. Changes to Terms">
            <p>
              We may update these Terms from time to time. We will notify you of material changes by email or
              through the Service. Your continued use of the Service after such changes constitutes acceptance
              of the updated Terms.
            </p>
          </LegalSection>

          <LegalSection title="12. Contact Information">
            <p>
              If you have questions about these Terms, please contact us through the support channels available
              in your admin console or via email at the contact information provided to your agency.
            </p>
          </LegalSection>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PRIVACY POLICY
// ============================================================================

export function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <div className="mk-container">
        <header className="legal-header">
          <Link to="/" className="legal-back">
            <SafeIcon icon={Icons.ArrowLeft} />
            Back to home
          </Link>
          <h1>Privacy Policy</h1>
          <p className="legal-subtitle">
            How we collect, use, and protect your personal data in compliance with UK GDPR
          </p>
          <LastUpdated />
        </header>

        <div className="legal-content">
          <LegalSection title="1. Data Controller">
            <p>
              During is a client portal platform operated by during.uk. We act as a data controller for personal
              data processed through the Service. This Privacy Policy explains how we collect, use, store, and
              protect your information in compliance with the UK General Data Protection Regulation (UK GDPR).
            </p>
          </LegalSection>

          <LegalSection title="2. Information We Collect">
            <p>We collect the following types of personal data:</p>
            <LegalList items={[
              '<strong>Agency and staff accounts:</strong> name, email address, role, and account activity logs',
              '<strong>Client portal users:</strong> name, email address (for magic-link authentication), and engagement data',
              '<strong>Agency clients:</strong> contact details, property information, job history, and communications stored on behalf of your agency',
              '<strong>Invoice and billing data:</strong> payment information processed securely via Stripe (we do not store card details)',
              '<strong>Documents and media:</strong> invoices, certificates, quotes, job sheets, and photos uploaded by your agency and engineers',
              '<strong>Usage data:</strong> portal access logs, feature usage, and performance metrics'
            ]} />
          </LegalSection>

          <LegalSection title="3. Legal Basis for Processing">
            <p>Under UK GDPR, we rely on the following lawful bases for processing your personal data:</p>
            <LegalList items={[
              '<strong>Contract:</strong> we process data necessary to provide the Service under our subscription agreement',
              '<strong>Legitimate interests:</strong> we process data for platform security, fraud prevention, service improvement, and analytics (where proportionate and not overridden by your rights)',
              '<strong>Legal obligation:</strong> we retain certain data to comply with accounting, tax, and regulatory requirements'
            ]} />
          </LegalSection>

          <LegalSection title="4. Data Processors and Subprocessors">
            <p>We engage with third-party service providers to help operate the Service:</p>
            <LegalList items={[
              '<strong>Hosting and infrastructure:</strong> virtual private servers (VPS) for application hosting and data storage',
              '<strong>Transactional email:</strong> email service provider for delivery of magic links, notifications, and alerts',
              '<strong>Payment processing:</strong> Stripe processes online payments (card details are handled directly by Stripe, not stored by us)',
              '<strong>Our processors are carefully selected, bound by data protection agreements, and based in the UK or EEA where data transfers are governed by adequacy decisions or standard contractual clauses'
            ]} />
          </LegalSection>

          <LegalSection title="5. Data Retention">
            <p>We retain personal data for different periods depending on its purpose:</p>
            <LegalList items={[
              '<strong>Active accounts:</strong> retained while your account is active',
              '<strong>Financial records:</strong> retained for 7 years in compliance with UK tax law',
              '<strong>Job and certificate records:</strong> retained in accordance with industry regulations and your agency policies',
              '<strong>Deleted accounts:</strong> personal data is deleted or anonymised within a reasonable period, except where legal or regulatory obligations require longer retention'
            ]} />
          </LegalSection>

          <LegalSection title="6. Your Rights Under UK GDPR">
            <p>As a data subject, you have the following rights:</p>
            <LegalList items={[
              '<strong>Right of access:</strong> request a copy of the personal data we hold about you',
              '<strong>Right to rectification:</strong> request correction of inaccurate or incomplete data',
              '<strong>Right to erasure:</strong> request deletion of your personal data (subject to legal and operational constraints)',
              '<strong>Right to restrict processing:</strong> request that we limit how we use your data',
              '<strong>Right to data portability:</strong> request a machine-readable copy of your data',
              '<strong>Right to object:</strong> object to processing based on legitimate interests',
              '<strong>Right to withdraw consent:</strong> withdraw any consent you have given (where processing is based on consent)'
            ]} />
            <p style={{ marginTop: 12 }}>
              To exercise these rights, please contact your agency administrator or use the support channels
              available in the admin console. We will respond to your request within one month, extendable in
              complex cases.
            </p>
          </LegalSection>

          <LegalSection title="7. Cookies and Tracking">
            <p>
              The Service uses strictly essential cookies for authentication and session management. These
              cookies are necessary for the Service to function and cannot be disabled. We do not use:
            </p>
            <LegalList items={[
              'Advertising cookies',
              'Analytics or tracking cookies',
              'Third-party tracking pixels or beacons',
              'Social media tracking'
            ]} />
            <p style={{ marginTop: 12 }}>
              Essential cookies are stored for the duration of your session and are deleted when you log out.
              The magic-link authentication system issues temporary tokens that expire after use.
            </p>
          </LegalSection>

          <LegalSection title="8. Data Security">
            <p>We implement appropriate technical and organisational measures to protect your data, including:</p>
            <LegalList items={[
              'Encryption in transit (HTTPS/TLS) for all data transfers',
              'Secure authentication with magic links and password-based accounts',
              'Access controls and role-based permissions',
              'Regular security reviews and updates',
              'Secure hosting with VPS providers'
            ]} />
            <p style={{ marginTop: 12 }}>
              However, no method of transmission over the Internet is 100% secure. While we strive to protect
              your data, we cannot guarantee absolute security.
            </p>
          </LegalSection>

          <LegalSection title="9. International Data Transfers">
            <p>
              Your data is stored and processed primarily within the United Kingdom. Where data is transferred
              to the European Economic Area (EEA) or other jurisdictions, we ensure appropriate safeguards are
              in place, such as adequacy decisions or standard contractual clauses as required by UK GDPR.
            </p>
          </LegalSection>

          <LegalSection title="10. Children's Privacy">
            <p>
              The Service is not intended for children under the age of 13. We do not knowingly collect personal
              data from children. If you become aware that a child has provided us with personal data, please
              contact us immediately.
            </p>
          </LegalSection>

          <LegalSection title="11. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or for
              other operational, legal, or regulatory reasons. We will notify you of material changes by email
              or through the Service. Your continued use of the Service after such changes constitutes acceptance
              of the updated Policy.
            </p>
          </LegalSection>

          <LegalSection title="12. Contact Information">
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or your personal data,
              please contact your agency administrator or use the support channels available in the admin console.
              For data subject requests that your agency cannot resolve, we will provide direct contact information
              for during.uk's data protection coordination.
            </p>
          </LegalSection>
        </div>
      </div>
    </div>
  );
}